import React, { useState } from 'react';
import StageGates from '../components/dashboard/StageGates';
import MetricsChart from '../components/dashboard/MetricsChart';
import RiskRegister from '../components/dashboard/RiskRegister';
import LaunchSequenceModal from '../components/LaunchSequenceModal';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const MissionBoard = () => {
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [systemState, setSystemState] = useState('active'); // 'active', 'empty', 'warning'

  const stats = {
    active: [
      { label: 'ASSET READINESS', value: '84%', icon: 'CheckCircle', color: 'text-rocket-500' },
      { label: 'PENDING TASKS', value: '12', icon: 'List', color: 'text-ice' },
      { label: 'STAKEHOLDER SYNC', value: '92%', icon: 'Users', color: 'text-blue-400' },
      { label: 'RISK SCORE', value: 'LOW', icon: 'Shield', color: 'text-green-500' },
    ],
    warning: [
      { label: 'ASSET READINESS', value: '42%', icon: 'AlertTriangle', color: 'text-red-500' },
      { label: 'PENDING TASKS', value: '48', icon: 'Clock', color: 'text-yellow-500' },
      { label: 'STAKEHOLDER SYNC', value: '15%', icon: 'Users', color: 'text-red-400' },
      { label: 'RISK SCORE', value: 'CRITICAL', icon: 'Zap', color: 'text-red-600' },
    ]
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">
      {/* Header & State Control */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
        <div>
          <h2 className="text-3xl font-bold text-ice tracking-tight">Mission Board</h2>
          <div className="flex items-center space-x-3 mt-1">
            <span className="text-xs text-slate-500 font-mono uppercase tracking-widest">System Status:</span>
            <div className="flex bg-slate-900/80 p-1 rounded border border-slate-800">
              {['active', 'empty', 'warning'].map(state => (
                <button 
                  key={state}
                  onClick={() => setSystemState(state)}
                  className={`px-3 py-0.5 text-[10px] font-mono rounded uppercase transition-all ${
                    systemState === state ? 'bg-rocket-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {state}
                </button>
              ))}
            </div>
          </div>
        </div>
        <button 
          onClick={() => setShowLaunchModal(true)}
          className="w-full lg:w-auto bg-rocket-600 hover:bg-rocket-500 text-white px-8 py-3 rounded font-bold flex items-center justify-center transition-all shadow-[0_0_20px_rgba(234,88,12,0.3)] hover:scale-105"
        >
          <SafeIcon name="Power" className="mr-2" />
          AUTHORIZE STAGE GATE
        </button>
      </div>

      {systemState === 'empty' ? (
        <div className="h-[60vh] flex flex-col items-center justify-center glass-panel rounded-2xl border-dashed border-2 border-slate-800">
          <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mb-6">
            <SafeIcon name="Plus" className="text-4xl text-slate-700" />
          </div>
          <h3 className="text-xl font-bold text-ice">No Mission Active</h3>
          <p className="text-slate-500 font-mono mt-2 text-sm">INITIALIZE NEW LAUNCH PROTOCOL TO BEGIN</p>
          <button className="mt-8 px-6 py-2 bg-slate-800 hover:bg-slate-700 text-ice rounded font-semibold transition-all">
            Start New Mission
          </button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          {/* Stage Gates */}
          <div className="glass-panel p-8 rounded-2xl tech-border relative overflow-hidden">
             <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                <SafeIcon name="Target" size={120} />
             </div>
            <h3 className="text-xs font-mono text-slate-500 mb-8 flex items-center uppercase tracking-[0.2em]">
              <SafeIcon name="GitCommit" className="mr-2 text-rocket-500" />
              Active Sequence Gates
            </h3>
            <StageGates isWarning={systemState === 'warning'} />
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {(systemState === 'warning' ? stats.warning : stats.active).map((stat, i) => (
              <div key={i} className="glass-panel p-5 rounded-xl border border-slate-800/50 hover:border-slate-700 transition-all">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{stat.label}</span>
                  <SafeIcon name={stat.icon} className={stat.color} />
                </div>
                <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Main Content Area */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 glass-panel p-6 rounded-2xl border border-slate-800/50">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h3 className="text-sm font-mono text-ice flex items-center uppercase tracking-widest">
                    Performance Snapshot
                  </h3>
                  <p className="text-xs text-slate-500 font-mono mt-1">REAL-TIME TRACTION TELEMETRY</p>
                </div>
                <div className="flex space-x-2">
                  <div className="flex items-center space-x-1 text-[10px] font-mono text-rocket-500">
                    <span className="w-2 h-2 rounded-full bg-rocket-500"></span>
                    <span>CONVERSIONS</span>
                  </div>
                  <div className="flex items-center space-x-1 text-[10px] font-mono text-blue-500">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span>TRAFFIC</span>
                  </div>
                </div>
              </div>
              <div className="h-72 w-full">
                <MetricsChart variant={systemState} />
              </div>
            </div>

            <div className="space-y-6">
              <RiskRegister isWarning={systemState === 'warning'} />
              
              <div className="glass-panel p-5 rounded-2xl border border-slate-800/50 bg-gradient-to-br from-slate-900/40 to-transparent">
                <h3 className="text-xs font-mono text-slate-500 mb-4 flex items-center uppercase tracking-widest">
                  Recent Comms
                </h3>
                <div className="space-y-4">
                  {[
                    { user: 'SV', msg: 'Hero video approved', time: '2m ago' },
                    { user: 'MT', msg: 'Ad spend scaled +15%', time: '14m ago' },
                    { user: 'BW', msg: 'API limits checked', time: '1h ago' }
                  ].map((chat, i) => (
                    <div key={i} className="flex space-x-3 items-start">
                      <div className="w-7 h-7 rounded bg-slate-800 flex items-center justify-center text-[10px] font-bold text-rocket-400 border border-slate-700">{chat.user}</div>
                      <div>
                        <p className="text-xs text-ice leading-tight">{chat.msg}</p>
                        <p className="text-[10px] font-mono text-slate-600 mt-1 uppercase">{chat.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {showLaunchModal && <LaunchSequenceModal onClose={() => setShowLaunchModal(false)} />}
    </div>
  );
};

export default MissionBoard;